import { datadogRum } from '@datadog/browser-rum';
import { get as _get } from 'lodash';
import { v4 as uuid } from 'uuid';

import { CreateDraftClaimModel, SubmitQuestionApiModel } from '../model/create-draft-claim-request';
import { OpCo } from '../types/type';

export async function createDraftClaim(
  urlKey: string,
  sessionId: string,
  custKey: string,
  draftClaimBody: CreateDraftClaimModel,
  lob?: string,
  partner?: string,
  policySource?: string
) {
  try {
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', 'Bearer ' + custKey);
    headers.append('content-type', 'application/json');
    headers.append('afe-trace-id', uuid());

    // Pass converse-specific headers to enable API to use them for converseNext call
    // This ensures consistency between API's converseNext call and any UI fallback converseNext calls
    if (lob) {
      headers.append('line-of-business', lob);
    }

    if (partner) {
      // Apply the same mapping logic we use for converseNext to ensure consistency
      let partnerId = '';
      switch (partner) {
        case OpCo.HOMESITE:
          partnerId = OpCo.HOMESITE; // Map to homesite
          break;
        default:
          partnerId = partner; // All other partners pass through as-is
          break;
      }
      headers.append('partner-id', partnerId);
    }

    if (policySource) {
      headers.append('policy-source', policySource);
    }

    const URI = `${process.env['NX_DCV2_API_BASE_URL']}draftclaim/create`;
    const response = await fetch(URI, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(draftClaimBody),
    });
    const responseData = await response.json();
    const { statusCode } = responseData;
    const { error } = responseData;
    const { message } = responseData;
    if (_get(responseData, 'status.statusCode', '') >= 200) {
      datadogRum.setGlobalContextProperty('draftClaimNumber', responseData.data.claimNumber); // Adding this allows us to filter sessions based upon @context.draftClaimNumber value in DataDog Session Explorer
      // Option 6: Track when draft claim is created
      datadogRum.setGlobalContextProperty('sessionStatus', 'draft-claim-created');
      return responseData.data;
    } else {
      return { error, statusCode, message };
    }
  } catch (error) {
    console.log('createDraftClaim:error', error);
    return error;
  }
}

export async function converseNext(
  urlKey: string,
  sessionId: string,
  custKey: string,
  lob: string,
  partner: string,
  draftClaimNumber: string,
  converseBody: SubmitQuestionApiModel | Record<string, never>,
  policySource: string | ''
) {
  try {
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', 'Bearer ' + custKey);
    headers.append('content-type', 'application/json');
    headers.append('cust-key', generateId());
    headers.append('afe-trace-id', uuid());

    if (policySource) {
      headers.append('policy-source', policySource);
    }

    if (lob) {
      headers.append('line-of-business', lob);
    }
    let partnerId = '';
    switch (partner) {
      case OpCo.HOMESITE:
        partnerId = OpCo.HOMESITE; // Send 'homesite' as partner-id for 'polaris-homesite-lemonade' as they use same question set.
        break;
      default:
        partnerId = partner;
        break;
    }

    if (partner) {
      headers.append('partner-id', partnerId);
    }
    const URI = `${process.env['NX_DCV2_API_BASE_URL']}converse/next/${draftClaimNumber}`;
    const response = await fetch(URI, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(converseBody),
    });
    const responseData = await response.json();
    const { statusCode } = responseData;
    const { error } = responseData;
    const { message } = responseData;
    if (_get(responseData, 'status.statusCode', '') >= 200) {
      return responseData;
    } else {
      // errorCode/claimNumber are set when the draft was already submitted (E1PCDC-7008),
      // so the caller can route to the full claim instead of an error page.
      const { errorCode, claimNumber } = responseData;
      return { error, statusCode, message, errorCode, claimNumber };
    }
  } catch (error) {
    console.log('converseNext:error', error);
    return error;
  }
}

export async function converseBack(
  urlKey: string,
  sessionId: string,
  custKey: string,
  lob: string,
  partner: string,
  draftClaimNumber: string,
  questionNumber: string,
  policySource: string | ''
) {
  try {
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', 'Bearer ' + custKey);
    headers.append('content-type', 'application/json');
    headers.append('cust-key', generateId());
    headers.append('afe-trace-id', uuid());

    if (policySource) {
      headers.append('policy-source', policySource);
    }

    if (lob) {
      headers.append('line-of-business', lob);
    }

    if (partner) {
      headers.append('partner-id', partner);
    }

    const URI = `${process.env['NX_DCV2_API_BASE_URL']}converse/previous/${draftClaimNumber}/${questionNumber}`;
    const response = await fetch(URI, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({}),
    });
    const responseData = await response.json();
    const { statusCode } = responseData;
    const { error } = responseData;
    const { message } = responseData;

    if (_get(responseData, 'status.statusCode', '') >= 200) {
      return responseData;
    } else {
      return { error, statusCode, message };
    }
  } catch (error) {
    console.log('converseBack:error', error);
    return error;
  }
}

export async function converseEdit(
  urlKey: string,
  sessionId: string,
  custKey: string,
  lob: string,
  partner: string,
  draftClaimNumber: string,
  questionNumber: string,
  policySource: string | ''
) {
  try {
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', 'Bearer ' + custKey);
    headers.append('content-type', 'application/json');
    headers.append('cust-key', Math.random().toString());
    headers.append('afe-trace-id', uuid());

    if (lob) {
      headers.append('line-of-business', lob);
    }

    if (policySource) {
      headers.append('policy-source', policySource);
    }

    if (partner) {
      headers.append('partner-id', partner);
    }

    const URI = `${process.env['NX_DCV2_API_BASE_URL']}converse/${draftClaimNumber}/${questionNumber}`;
    const response = await fetch(URI, {
      method: 'GET',
      headers: headers,
    });
    const responseData = await response.json();
    const { statusCode } = responseData;
    const { error } = responseData;
    const { message } = responseData;

    if (_get(responseData, 'status.statusCode', '') >= 200) {
      return responseData;
    } else {
      return { error, statusCode, message };
    }
  } catch (error) {
    console.log('converseEdit:error', error);
    return error;
  }
}

export async function converseSubmit(
  urlKey: string,
  sessionId: string,
  custKey: string,
  lob: string,
  partner: string,
  draftClaimNumber: string,
  policySource: string | ''
) {
  try {
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', 'Bearer ' + custKey);
    headers.append('content-type', 'application/json');
    headers.append('cust-key', Math.random().toString());
    headers.append('afe-trace-id', uuid());

    if (lob) {
      headers.append('line-of-business', lob);
    }

    if (policySource) {
      headers.append('policy-source', policySource);
    }

    if (partner) {
      headers.append('partner-id', partner);
    }

    const URI = `${process.env['NX_DCV2_API_BASE_URL']}converse/submit/${draftClaimNumber}`;
    const response = await fetch(URI, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({}),
    });
    const responseData = await response.json();
    if (response.status >= 200 && response.status <= 299) {
      return responseData;
    } else if (response.status >= 400 && response.status <= 499) {
      const { statusCode } = responseData;
      const { error } = responseData;
      const { message } = responseData;
      return { error, statusCode, message };
    } else if (response.status === 504) {
      return { statusCode: 504 };
    }
  } catch (error) {
    console.log('converseSubmit:error', error);
    return error;
  }
}

export async function deleteDraft(urlKey: string, sessionId: string, custKey: string, draftClaimNumber: string) {
  const headers = new Headers();
  headers.append('urlid', urlKey);
  headers.append('afe-session-id', sessionId);
  headers.append('Authorization', 'Bearer ' + custKey);
  headers.append('content-type', 'application/json');
  headers.append('afe-trace-id', uuid());

  const URI = `${process.env['NX_DCV2_API_BASE_URL']}draftclaim/${draftClaimNumber}`;

  try {
    const response = await fetch(URI, {
      method: 'DELETE',
      headers: headers,
    });
    const responseData = await response.json();
    console.log('responseData:', responseData);
    if (_get(responseData, 'status.statusCode', '') === 200) {
      // The delete has been successful, so we can return to exit out of the retry loop.
      return responseData;
    } else {
      // We've received an error after exceeding our maximum retry timeout time, so exit the loop returning the error information.
      const { statusCode } = responseData;
      const { error } = responseData;
      const { message } = responseData;
      return { error, statusCode, message };
    }
  } catch (error) {
    console.log('deleteDraft:error', error);
    return error;
  }
}

export async function createSafelite(
  urlKey: string,
  sessionId: string,
  custKey: string,
  partnerId: string,
  reqBody: any
) {
  try {
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', 'Bearer ' + custKey);
    headers.append('Accept', 'text/html');
    headers.append('observe', 'body');
    headers.append('content-type', 'application/json');
    headers.append('afe-trace-id', uuid());

    const safeliteUrl = process.env['NX_DCV2_SAFELITE_URL'];
    if (!safeliteUrl) {
      console.error('NX_DCV2_SAFELITE_URL environment variable is not set');
      return null;
    }
    const URI = `${
      process.env['NX_DCV2_API_BASE_URL']
    }safelite/v1/safelitetoken?redirect=true&postURL=${encodeURIComponent(safeliteUrl)}&clientTag=${getClientTag(
      partnerId
    )}`;
    const response = await fetch(URI, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(reqBody),
    });
    if (response.status >= 200 && response.status <= 299) {
      const responseData = await response.text();
      return responseData;
    } else {
      // Cannot pass the 'erorrInfo:ErrorInfo' object due to the different implementation in the network layer for this API. The response is not JSON to be parsed.
      return null;
    }
  } catch (error) {
    console.log('createSafelite:error', error);
    return null;
  }
}

function getClientTag(partnerId: string) {
  switch (partnerId) {
    case OpCo.AMFAM:
    case OpCo.AMFAM_THIRD_PARTY:
      return 'D2315E43-A4CC-447C-AD77-233BCE008E97';
    case OpCo.MSA:
    case OpCo.MSA_THIRD_PARTY:
      return 'CF0695D4-BD81-462E-BB9A-CF30B0F4E221';
    default:
      return 'D2315E43-A4CC-447C-AD77-233BCE008E97';
  }
}

function generateId() {
  function S4() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }
  const guid = (S4() + S4() + '-' + S4() + S4() + '-' + S4() + '-' + S4() + S4() + S4()).toLowerCase();
  return guid;
}
